import { type LegalSlug, legalSlugFor } from "@/content/legal";
import { LANDING_PATHS } from "@/content/landing/paths";

/**
 * Where a marketing href actually goes on this origin.
 *
 * The published pages link each other root-relative (`/privacy-policy`,
 * `/terms`) because they ARE the marketing site. Here the same string resolves
 * against this origin, so a destination we serve has to become a route link and
 * one we do not has to name the host, or it 404s. Used by the footer and by
 * every anchor inside the legal documents, which cite each other by URL.
 */
const SITE = "https://www.webshare.io";

type MarketingDestination =
  | { internal: true; to: LegalRoutePath }
  | { internal: false; href: string; newTab: boolean };

/**
 * The ten route paths, as literals the router's `to` accepts. Built from
 * `LEGAL_SLUGS` so it cannot list a page that does not exist, and `satisfies`
 * so it cannot omit one.
 */
const LEGAL_ROUTE_PATH = {
  terms: "/terms",
  "privacy-policy": "/privacy-policy",
  "about-us": "/about-us",
  "compliance-policy": "/compliance-policy",
  "fair-usage-policy": "/fair-usage-policy",
  "intellectual-property": "/intellectual-property",
  "privacy-notice-for-california-residents":
    "/privacy-notice-for-california-residents",
  "privacy-notice-for-eea-switzerland-and-uk":
    "/privacy-notice-for-eea-switzerland-and-uk",
  "affiliate-agreement": "/affiliate-agreement",
  "master-services-agreement": "/master-services-agreement",
} as const satisfies Record<LegalSlug, `/${LegalSlug}`>;

type LegalRoutePath = (typeof LEGAL_ROUTE_PATH)[LegalSlug];

/**
 * The marketing path an href points at, or null if it leaves the site.
 *
 * Naming webshare.io on a path this app now serves sends a reader out of the
 * app and back to Webflow, which it did for twenty-one destinations we had
 * already built. That is why the footer and the header menus leave their `SITE`
 * prefix empty; the genuinely external links (apidocs, help, status) name their
 * own hosts and are untouched.
 */
function sitePath(href: string): string | null {
  if (href.startsWith("/")) {
    return href;
  }
  if (href.startsWith(`${SITE}/`)) {
    return href.slice(SITE.length);
  }
  return null;
}

/**
 * The path families this app serves.
 *
 * Content written for the published site names that host on 116 links, and
 * every one of them for a page we now render ourselves sent the reader back to
 * Webflow. Matching on the shape of the path rather than on a list of every
 * slug keeps this true as pages are added: `/features/anything` is ours
 * because the whole family is.
 */
const SERVED = [
  // The marketing home's route; on the marketing host it is served at `/`.
  /^\/home$/,
  /^\/features(\/[\w-]+)?$/,
  /^\/use-cases(\/[\w-]+)?$/,
  /^\/proxy-locations\/[\w-]+$/,
  /^\/academy(\/.+)?$/,
  /^\/academy-article\/[\w-]+$/,
  /^\/blog(\/[\w-]+)?$/,
  /^\/blog-categories\/[\w-]+$/,
  /^\/author\/[\w-]+$/,
  /^\/use-cases-categories(\/[\w-]+)?$/,
  // The campaign pages ads and partners link to.
  /^\/l\/[\w-]+$/,
  /^\/pricing$/,
  /^\/mcp-server$/,
  /^\/proxy-checker$/,
];

/** Whether this app answers the path, so a link to it should stay inside. */
export function servesMarketingPath(path: string): boolean {
  const clean = path.split("?")[0].replace(/\/$/, "") || "/";
  if (LANDING_PATHS.includes(clean)) {
    return true;
  }
  if (legalSlugFor(clean.replace(/^\//, ""))) {
    return true;
  }
  return SERVED.some((shape) => shape.test(clean));
}

/**
 * The href to print for a destination written against the published site.
 *
 * Root-relative when we serve it, untouched when we do not.
 */
export function marketingHref(href: string): string {
  // Already ours: `/register` and `/login` are this app's own routes and are
  // not marketing paths, so naming the published host for them would send a
  // reader signing up to Webflow instead.
  if (href.startsWith("/")) {
    return href;
  }
  const path = sitePath(href);
  if (path === null) {
    return href;
  }
  return servesMarketingPath(path) ? path : href;
}

/**
 * The attributes an anchor takes where the published one opens a new tab. The
 * choice is the content's, link by link, as the footer and the home page
 * already carry it; `rel` guards the opener as those anchors do.
 */
export function newTabProps(link: { newTab?: boolean } | null | undefined): {
  target?: "_blank";
  rel?: "noopener";
} {
  return link?.newTab === true ? { target: "_blank", rel: "noopener" } : {};
}

export function marketingDestination(href: string): MarketingDestination {
  const path = sitePath(href);
  if (path === null) {
    // mailto:, help.webshare.io, patent registers: everything off-site opens in
    // a new tab, which is what the published anchors that carry one do.
    return { internal: false, href, newTab: !href.startsWith("mailto:") };
  }
  // A cited URL is sometimes written with a trailing slash and sometimes not;
  // both name the same page.
  const slug = legalSlugFor(path.replace(/^\//, "").replace(/\/$/, ""));
  if (slug) {
    return { internal: true, to: LEGAL_ROUTE_PATH[slug] };
  }
  // Only the ten legal pages have a route literal to name, so anything else we
  // serve stays an anchor; naming the host for it would land the reader on
  // Webflow.
  return {
    internal: false,
    href: servesMarketingPath(path) ? path : `${SITE}${path}`,
    newTab: false,
  };
}
